<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[10060] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      @keydown.escape.window="$emit('close')"
    >
      <div class="cliente-submodal cliente-recebivel-modal shadow-2xl w-full max-w-lg border flex flex-col" @click.stop>
        <header class="cliente-submodal__header cliente-recebivel-modal__header">
          <div class="cliente-recebivel-modal__icon-wrap shrink-0">
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <h3 class="cliente-recebivel-modal__title">{{ isEdit ? t('superAdmin.billing.editCobranca') : t('superAdmin.billing.addCobranca') }}</h3>
            <p class="cliente-recebivel-modal__subtitle">{{ t('superAdmin.billing.formSubtitle') }}</p>
          </div>
          <button type="button" class="cliente-recebivel-modal__close" :aria-label="t('common.close')" @click="$emit('close')">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </header>

        <div class="cliente-submodal__body space-y-4">
          <div class="cliente-recebivel-modal__valor-block">
            <label class="cliente-recebivel-modal__field-label">
              {{ t('superAdmin.billing.valor') }} <span class="cliente-recebivel-modal__req">*</span>
            </label>
            <div class="cliente-recebivel-modal__valor-input" :class="{ 'cliente-recebivel-modal__valor-input--error': fieldErrors.valor }">
              <img :src="moneyIcon" alt="" class="cliente-recebivel-modal__money-icon" aria-hidden="true" />
              <input
                :value="valorDisplay"
                type="text"
                inputmode="decimal"
                class="cliente-recebivel-modal__valor-field"
                :placeholder="t('superAdmin.billing.valorPlaceholder')"
                @input="onValorInput"
              />
            </div>
            <p v-if="fieldErrors.valor" class="cliente-recebivel-modal__error">{{ fieldErrors.valor }}</p>
          </div>

          <ClienteFormField
            v-model="local.descricao"
            :label="t('superAdmin.billing.descricao')"
            multiline
            :rows="2"
            :placeholder="t('superAdmin.billing.descricaoPlaceholder')"
          />

          <ClienteFormField
            v-model="local.dataVencimento"
            :label="t('superAdmin.billing.vencimento')"
            type="date"
            required
            :error="fieldErrors.dataVencimento"
            @update:model-value="fieldErrors.dataVencimento = ''"
          />

          <div v-if="!isEdit" class="cliente-recebivel-modal__rec-block">
            <button
              type="button"
              class="cliente-recebivel-modal__rec-card"
              :class="{ 'cliente-recebivel-modal__rec-card--active': local.mensal }"
              @click="local.mensal = !local.mensal"
            >
              <span class="cliente-recebivel-modal__rec-card__icon" aria-hidden="true">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
              </span>
              <span class="cliente-recebivel-modal__rec-card__text">
                <strong>{{ t('superAdmin.billing.mensalToggle') }}</strong>
                <small>{{ t('superAdmin.billing.mensalSub') }}</small>
              </span>
              <span class="cliente-recebivel-modal__rec-card__switch" :aria-pressed="local.mensal" />
            </button>
            <div v-if="local.mensal" class="cliente-recebivel-modal__rec-body">
              <span class="cliente-recebivel-modal__rec-body-label">{{ t('superAdmin.billing.mensalModoLabel') }}</span>
              <div class="cliente-recebivel-modal__rec-modes" role="radiogroup" :aria-label="t('superAdmin.billing.mensalModoLabel')">
                <button type="button" class="cliente-recebivel-modal__rec-mode-btn" :class="{ 'cliente-recebivel-modal__rec-mode-btn--active': recMode === 'vezes' }" @click="recMode = 'vezes'">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                  {{ t('superAdmin.billing.mensalVezes') }}
                </button>
                <button type="button" class="cliente-recebivel-modal__rec-mode-btn" :class="{ 'cliente-recebivel-modal__rec-mode-btn--active': recMode === 'sempre' }" @click="recMode = 'sempre'">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                  {{ t('superAdmin.billing.mensalSempre') }}
                </button>
              </div>
              <div v-if="recMode === 'vezes'" class="cliente-recebivel-modal__rec-qty">
                <label class="cliente-recebivel-modal__field-label" for="sa-cobranca-rec-qty">{{ t('superAdmin.billing.mensalQuantidade') }}</label>
                <div class="cliente-recebivel-modal__qty-wrap">
                  <button type="button" class="cliente-recebivel-modal__qty-step" :disabled="local.repeticoes <= 2" @click="bumpRepeticoes(-1)">−</button>
                  <input id="sa-cobranca-rec-qty" v-model.number="local.repeticoes" type="number" min="2" max="120" class="cliente-recebivel-modal__qty-input" />
                  <button type="button" class="cliente-recebivel-modal__qty-step" :disabled="local.repeticoes >= 120" @click="bumpRepeticoes(1)">+</button>
                  <span class="cliente-recebivel-modal__qty-suffix">{{ t('superAdmin.billing.mensalMesesSuffix') }}</span>
                </div>
              </div>
              <p v-else class="cliente-recebivel-modal__rec-hint">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                {{ t('superAdmin.billing.mensalSempreHint') }}
              </p>
            </div>
          </div>
        </div>

        <footer class="cliente-submodal__footer cliente-recebivel-modal__footer">
          <button type="button" class="btn-secondary" @click="$emit('close')">{{ t('common.cancel') }}</button>
          <button type="button" class="btn-primary cliente-recebivel-modal__save" :disabled="!canSave" @click="save">
            {{ t('common.save') }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ClienteFormField from '../cliente/ClienteFormField.vue'
import { parseCurrencyMask, formatCurrencyMask } from '@/utils/currencyMask'
import moneyIcon from '@/assets/money-icon.png'
import '@/assets/styles/cliente-modal-theme.css'
import '@/assets/styles/cliente-recebivel-modal.css'

const props = defineProps({
  open: { type: Boolean, default: false },
  initial: { type: Object, default: null },
  defaultValor: { type: Number, default: null },
  defaultVencimento: { type: String, default: '' }
})

const emit = defineEmits(['close', 'save'])
const { t, locale } = useI18n()

const empty = () => ({ valor: null, dataVencimento: '', descricao: '', mensal: false, repeticoes: 6 })
const local = ref(empty())
const recMode = ref('vezes')
const valorDisplay = ref('')
const fieldErrors = ref({ valor: '', dataVencimento: '' })

const isEdit = computed(() => props.initial != null)
const canSave = computed(() => Number(local.value.valor || 0) > 0 && !!local.value.dataVencimento)

function bumpRepeticoes (delta) {
  const next = Math.min(120, Math.max(2, Number(local.value.repeticoes || 2) + delta))
  local.value.repeticoes = next
}

function onValorInput (event) {
  const parsed = parseCurrencyMask(event?.target?.value ?? '')
  local.value.valor = parsed > 0 ? parsed : null
  valorDisplay.value = parsed > 0 ? formatCurrencyMask(parsed, locale.value) : ''
  fieldErrors.value.valor = ''
}

function save () {
  if (!canSave.value) return
  const payload = {
    valor: local.value.valor,
    descricao: local.value.descricao,
    dataVencimento: local.value.dataVencimento
  }
  if (!isEdit.value && local.value.mensal) {
    payload.parcelas = recMode.value === 'sempre' ? null : local.value.repeticoes
    payload.recorrenciaSempre = recMode.value === 'sempre'
  }
  emit('save', payload)
}

watch(() => props.open, (isOpen) => {
  if (!isOpen) return
  fieldErrors.value = { valor: '', dataVencimento: '' }
  if (props.initial) {
    local.value = {
      valor: props.initial.valor,
      dataVencimento: props.initial.dataVencimento || '',
      descricao: props.initial.descricao || '',
      mensal: false,
      repeticoes: 6
    }
    valorDisplay.value = props.initial.valor ? formatCurrencyMask(props.initial.valor, locale.value) : ''
  } else {
    local.value = {
      ...empty(),
      valor: props.defaultValor || null,
      dataVencimento: props.defaultVencimento || ''
    }
    valorDisplay.value = props.defaultValor ? formatCurrencyMask(props.defaultValor, locale.value) : ''
  }
  recMode.value = 'vezes'
}, { immediate: true })
</script>
